/**
 * frontend/src/modules/data-quality/lib/api.ts
 * API client for Data Quality endpoints — post P3 (2026-07-25) + post
 * D-TRUST-KPI-SUPPRIME (2026-07-27).
 *
 * Endpoints exposés :
 *   GET /data-quality/summary   — ancre semaine (post-P3 : payload minimal)
 *   GET /data-quality/ops-kpis  — Pilotage V2
 *
 * Retirés en vague dettes 3 (2026-07-27) : kpi-trust-score + kpi-trust-scores
 * (Trust KPI supprimé du produit — source visit_trust_scores droppée matinée,
 * pas de rebranchement DA-V2). Types KpiTrustScoreFilters/Data + fonctions
 * fetchKpiTrustScore / fetchKpiTrustScores supprimés.
 *
 * DECISIONS #157 · SSOT §11.8
 */

import { apiClient } from '@/lib/apiClient';

// ── DQ Summary — post-P3 : payload minimal (week_iso ancre + computed_at) ────
// Les compteurs merch_trust / pdv_trust / anomalies retirés avec les surfaces
// legacy qui les affichaient. Le widget sidebar (seul consommateur restant)
// n'utilisait déjà que week_iso comme ancre pour le hook V2.

export interface DqSummaryData {
  week_iso:    string;
  computed_at: string;
}

export async function fetchDqSummary(weekIso?: string): Promise<DqSummaryData> {
  const qs = weekIso ? `?week_iso=${encodeURIComponent(weekIso)}` : '';
  const res = await apiClient.get<DqSummaryData>(`/data-quality/summary${qs}`);
  return res.data;
}

// ── Ops KPIs (J2.5 §1.1) ─────────────────────────────────────────────────────

export interface OpsKpisQuery {
  period_weeks?: 4 | 8 | 12;
  region_id?:    string;
  channel_type?: 'retail' | 'wholesale';
}

export interface OpsKpisData {
  // #5 T1.3 D-DATA-ACCURACY-CADRE-HEBDO (22/07) — cadrans HEBDO : value_week +
  // prev_avg sur N sem via toggle. Doctrine « une seule unité de temps ».
  // trust_score_week + trust_score_prev_avg retirés en vague dettes 3
  // (D-TRUST-KPI-SUPPRIME, 2026-07-27).
  gps_match_rate_week_pct?:       number | null;
  gps_match_rate_prev_avg_pct?:   number | null;
  // Signaux actionnables OUVERTS (file d'attente CUMULATIVE, pas mesure hebdo).
  anomalies_open_actionable?:     number | null;
  weekly_coverage_week_pct?:      number | null;
  weekly_coverage_prev_avg_pct?:  number | null;
  // Cadrans hors périmètre (moyenne 12 sem inchangée)
  rfid_scan_rate_pct:             number | null;
  completeness_3way_pct:          number | null;
  // Hebdo par construction
  pdv_under_visited?:     number | null;
  pdv_under_visited_pct?: number | null;
  pdv_active_total?:      number | null;
}

export interface OpsKpisMeta {
  /** #5 T1.3 — semaine ancre (MAX(week_iso)) : où porte la mesure des cadrans hebdo. */
  anchor_week?:       string;
  /** Nombre effectif de semaines dans la comparaison (≤ period_weeks selon disponibilité). */
  comparison_weeks?:  number;
  period_weeks:       number;
  period_from:        string;
  period_to:          string;
  computed_at:        string;
  observations_count: number;
  filters: {
    region_id:    string | null;
    channel_type: string | null;
  };
}

export interface OpsKpisResponse {
  data: OpsKpisData;
  meta: OpsKpisMeta;
}

export async function fetchOpsKpis(q: OpsKpisQuery = {}): Promise<OpsKpisResponse> {
  const sp = new URLSearchParams();
  if (q.period_weeks)  sp.set('period_weeks',  String(q.period_weeks));
  if (q.region_id)     sp.set('region_id',     q.region_id);
  if (q.channel_type)  sp.set('channel_type',  q.channel_type);
  const qs = sp.toString();
  const res = await apiClient.get<OpsKpisResponse>(`/data-quality/ops-kpis${qs ? `?${qs}` : ''}`);
  return res.data;
}
